import type { PublishedAdvisorSummary } from '@agio/core/domain';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { api } from '../../api.js';
import { dataVersionState } from '../../state.js';
import { Skeleton } from '../Skeleton.js';
import {
  advisorSummaryPath, advisorSummaryState, type AdvisorFetch, type AdvisorSummaryState,
} from './advisorSummaryState.js';
import { AdvisorRichText } from './AdvisorRichText.js';
import { cn } from '../../utils.js';

/** "27.10.2026" — a summary's date, as the client reads it. */
export function fmtAdvisorDate(iso: string): string {
  return new Date(iso).toLocaleDateString('he-IL');
}

/**
 * The client's published summary, as one of four distinct states (see
 * advisorSummaryState.ts) — never a bare null that could mean "not yet".
 */
export function usePublishedAdvisorSummary(clientId: string | null): AdvisorSummaryState {
  const version = useRecoilValue(dataVersionState);
  const path = advisorSummaryPath(clientId);
  const [last, setLast] = useState<AdvisorFetch | null>(null);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    api
      .get<PublishedAdvisorSummary | null>(path)
      .then((data) => alive && setLast({ path, outcome: { ok: true, data } }))
      .catch((e: Error) => alive && setLast({ path, outcome: { ok: false, error: String(e.message ?? e) } }));
    return () => { alive = false; };
  }, [path, version]);
  return advisorSummaryState(path, last);
}

/**
 * The portal's card. Loading shows a placeholder and a failure says so — only
 * a server answer of "nothing published" shows nothing at all.
 */
export function AdvisorSummaryCard({ state }: { state: AdvisorSummaryState }) {
  if (state.status === 'none') return null;
  if (state.status === 'ready') return <AdvisorSummaryBlock summary={state.summary} />;
  if (state.status === 'error') {
    return (
      <section role="alert" className="card-block rounded-xl border border-destructive/50 bg-card p-5 text-sm">
        <p className="font-semibold">לא ניתן היה לטעון את סקירת היועץ</p>
        <p className="mt-1 text-muted-foreground" dir="auto">{state.error}</p>
      </section>
    );
  }
  return (
    <section aria-busy="true" aria-label="טוען סקירת יועץ" className="card-block rounded-xl border border-border bg-card p-5 sm:p-6">
      <Skeleton className="h-5 w-28" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="mt-2 h-3 w-4/5" />
    </section>
  );
}

/**
 * סקירת יועץ as the client sees it: the latest PUBLISHED text, not tied to any
 * month. Callers render it only for a published summary — with nothing
 * published there is no block at all, and never the main summary in its place.
 */
export function AdvisorSummaryBlock({
  summary,
  variant = 'portal',
}: {
  summary: PublishedAdvisorSummary;
  variant?: 'portal' | 'print';
}) {
  const print = variant === 'print';
  return (
    <section
      aria-labelledby="advisor-summary-title"
      className={
        print
          ? 'print-advisor flex flex-col gap-2'
          : 'card-block rounded-xl border border-border bg-card p-5 shadow-(--shadow-card) sm:p-6'
      }
    >
      <div className={cn('flex flex-wrap items-baseline gap-x-3 gap-y-1', print ? 'flex-col items-center' : 'justify-between')}>
        <h2 id="advisor-summary-title" className={print ? 'print-heading' : 'text-lg font-semibold tracking-tight'}>
          סקירת יועץ
        </h2>
        <p className="text-xs text-muted-foreground">
          עודכן ב־<time dateTime={summary.publishedAt}>{fmtAdvisorDate(summary.publishedAt)}</time>
        </p>
      </div>
      <AdvisorRichText doc={summary.body} />
    </section>
  );
}
